import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Briefcase, Plus, Users, Calendar, Shield, Search, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const DealRooms = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [dealRooms, setDealRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    participants: '',
    template: ''
  });

  const templates = [
    { id: 'dental-implant', name: 'Dental Implant', icon: '🦷', description: 'Single or multiple implant planning' },
    { id: 'full-arch', name: 'Full Arch Implant Therapy', icon: '🏥', description: 'All-on-4/All-on-6 planning' },
    { id: 'surgical-extraction', name: 'Surgical Extraction', icon: '⚕️', description: 'Complex extraction cases' },
    { id: 'wisdom-teeth', name: 'Wisdom Teeth', icon: '🦷', description: 'Third molar extraction' },
    { id: 'jaw-surgery', name: 'Jaw Surgery', icon: '🏥', description: 'Orthognathic surgery planning' },
    { id: 'bone-grafting', name: 'Bone Grafting', icon: '💀', description: 'Bone augmentation procedures' },
    { id: 'pathology', name: 'Pathology/Biopsy', icon: '🔬', description: 'Tissue sampling and analysis' },
    { id: 'tmj', name: 'TMJ Consultation', icon: '😬', description: 'Temporomandibular joint treatment' },
    { id: 'trauma', name: 'Trauma', icon: '🚑', description: 'Emergency trauma cases' },
    { id: 'nerve', name: 'Nerve', icon: '🧠', description: 'Nerve repair/grafting' },
    { id: 'other', name: 'Other', icon: '📋', description: 'Custom case type' }
  ];

  useEffect(() => {
    fetchDealRooms();
    fetchAvailableUsers();
  }, []);

  const fetchAvailableUsers = async () => {
    try {
      // Fetch providers, patients, and team members
      const [providersRes, patientsRes, teamRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/providers`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/patients`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/team-members`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const providers = await providersRes.json();
      const patients = await patientsRes.json();
      const team = await teamRes.json();

      const users = [
        ...(providers.providers || []).map(p => ({ ...p, type: 'Provider' })),
        ...(patients.patients || []).map(p => ({ ...p, type: 'Patient' })),
        ...(team.team_members || []).map(t => ({ ...t, type: 'Team' }))
      ];
      setAvailableUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchDealRooms = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deal-rooms`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setDealRooms(data.deal_rooms || []);
      }
    } catch (error) {
      console.error('Error fetching deal rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromTemplate = async (template) => {
    const templateData = {
      name: `${template.name} Case - ${new Date().toLocaleDateString()}`,
      description: template.description,
      participants: formData.participants.split(',').map(p => p.trim()).filter(p => p),
      template: template.id
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deal-rooms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(templateData)
      });

      const data = await response.json();
      if (data.success) {
        alert('Deal Room created from template!');
        setShowTemplateModal(false);
        setFormData({ name: '', description: '', participants: '', template: '' });
        fetchDealRooms();
      }
    } catch (error) {
      console.error('Error creating deal room:', error);
      alert('Error creating deal room');
    }
  };

  const handleCreateDealRoom = async () => {
    if (!formData.name || !formData.description) {
      alert('Please fill in name and description');
      return;
    }

    // Combine selected participants and manually entered emails
    const manualEmails = formData.participants.split(',').map(p => p.trim()).filter(p => p);
    const selectedEmails = selectedParticipants.map(p => p.email);
    const allParticipants = [...new Set([...selectedEmails, ...manualEmails])];

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/deal-rooms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          participants: allParticipants
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('Deal Room created successfully!');
        setShowCreateModal(false);
        setFormData({ name: '', description: '', participants: '', template: '' });
        setSelectedParticipants([]);
        fetchDealRooms();
      }
    } catch (error) {
      console.error('Error creating deal room:', error);
      alert('Error creating deal room');
    }
  };

  const toggleParticipant = (userItem) => {
    if (selectedParticipants.find(p => p.email === userItem.email)) {
      setSelectedParticipants(selectedParticipants.filter(p => p.email !== userItem.email));
    } else {
      setSelectedParticipants([...selectedParticipants, userItem]);
    }
  };

  const filteredRooms = dealRooms.filter(room => room.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading deal rooms...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0A1628] mb-2 flex items-center">
            <Briefcase className="h-8 w-8 mr-3 text-[#D4AF37]" />
            Deal Rooms
          </h1>
          <p className="text-gray-600">Secure collaboration spaces for complex cases</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => setShowTemplateModal(true)}>
            <Briefcase className="h-4 w-4 mr-2" />
            From Template
          </Button>
          <Button className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]" onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create New
          </Button>
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input placeholder="Search deal rooms..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
      </div>

      {filteredRooms.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRooms.map((room) => (
            <Card key={room.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/deal-rooms/${room.id}`)}>
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <CardTitle className="text-lg">{room.name}</CardTitle>
                  <Badge className="bg-green-100 text-green-800 border-green-200">{room.status}</Badge>
                </div>
                <p className="text-sm text-gray-600">{room.description}</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {room.participants && room.participants.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                        <Users className="h-4 w-4 mr-1" />Participants ({room.participants.length})
                      </p>
                      <div className="flex -space-x-2">
                        {room.participants.slice(0, 4).map((participant, idx) => (
                          <Avatar key={idx} className="border-2 border-white h-8 w-8">
                            <AvatarFallback className="bg-[#0A1628] text-[#D4AF37] text-xs">
                              {participant.name?.split(' ').map(n => n[0]).join('') || 'P'}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                        {room.participants.length > 4 && (
                          <div className="h-8 w-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs font-semibold">+{room.participants.length - 4}</div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm text-gray-600 pt-3 border-t">
                    <span>{room.filesCount || 0} files</span>
                    <span>{room.messagesCount || 0} messages</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    <Calendar className="h-3 w-3 inline mr-1" />
                    Created: {room.created || 'N/A'} • Last: {room.lastActivity || 'N/A'}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-24 text-center">
            <Shield className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Deal Rooms Yet</h3>
            <p className="text-gray-500 mb-6">Create your first secure collaboration space</p>
            <div className="flex justify-center space-x-3">
              <Button variant="outline" onClick={() => setShowTemplateModal(true)}>
                <Briefcase className="h-4 w-4 mr-2" />Use Template
              </Button>
              <Button className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]" onClick={() => setShowCreateModal(true)}>
                <Plus className="h-4 w-4 mr-2" />Create Deal Room
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Create Deal Room from Template</h2>
            <p className="text-gray-600 mb-6">Select a case type to create a pre-configured deal room</p>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              {templates.map((template) => (
                <Card key={template.id} className="cursor-pointer hover:shadow-lg hover:border-[#D4AF37] transition" onClick={() => { setFormData({...formData, template: template.id, name: `${template.name} Case`, description: template.description}); }}>
                  <CardContent className="pt-6 text-center">
                    <div className="text-4xl mb-3">{template.icon}</div>
                    <h3 className="font-semibold text-[#0A1628] mb-1">{template.name}</h3>
                    <p className="text-xs text-gray-600">{template.description}</p>
                    {formData.template === template.id && (
                      <Badge className="mt-3 bg-[#D4AF37] text-[#0A1628]">Selected</Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            {formData.template && (
              <div className="space-y-4 border-t pt-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Deal Room Name</label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Participants (Email Addresses)</label>
                  <Textarea placeholder="Enter email addresses separated by commas" rows={2} value={formData.participants} onChange={(e) => setFormData({...formData, participants: e.target.value})} />
                </div>
              </div>
            )}
            <div className="flex space-x-2 pt-4">
              <Button className="flex-1 bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]" onClick={() => { if (formData.template) { const template = templates.find(t => t.id === formData.template); handleCreateFromTemplate(template); } else { alert('Please select a template'); } }} disabled={!formData.template}>
                Create from Template
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => { setShowTemplateModal(false); setFormData({ name: '', description: '', participants: '', template: '' }); }}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Create New Deal Room</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Deal Room Name *</label>
                <Input placeholder="e.g., Complex Implant Case - Dr. Smith" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <Textarea placeholder="Brief description of the case" rows={4} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Select Participants</label>
                <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                  {availableUsers.length > 0 ? availableUsers.map((userItem, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={selectedParticipants.some(p => p.email === userItem.email)}
                          onChange={() => toggleParticipant(userItem)}
                          className="h-4 w-4"
                        />
                        <div>
                          <p className="text-sm font-medium">{userItem.name}</p>
                          <p className="text-xs text-gray-600">{userItem.email} • {userItem.type}</p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">{userItem.type}</Badge>
                    </div>
                  )) : (
                    <p className="text-sm text-gray-500 text-center py-4">No users available</p>
                  )}
                </div>
                {selectedParticipants.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selectedParticipants.map((p, idx) => (
                      <Badge key={idx} className="bg-blue-100 text-blue-800">
                        {p.name} <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => toggleParticipant(p)} />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Or Enter Email Addresses Manually</label>
                <Textarea placeholder="additional@email.com, another@email.com" rows={2} value={formData.participants} onChange={(e) => setFormData({...formData, participants: e.target.value})} />
                <p className="text-xs text-gray-500 mt-1">Optional: Add additional emails separated by commas</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800"><strong>Note:</strong> Only participants will have access. You can add more later.</p>
              </div>
              <div className="flex space-x-2 pt-4">
                <Button className="flex-1 bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]" onClick={handleCreateDealRoom}>
                  <Plus className="h-4 w-4 mr-2" />Create Deal Room
                </Button>
                <Button onClick={() => { setShowCreateModal(false); setFormData({ name: '', description: '', participants: '', template: '' }); }} variant="outline" className="flex-1">Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealRooms;